import { useInfiniteQuery } from 'react-query';
import { authFetch } from '../../CommonUtils/authFetch';
import { newsfeedQueryKeys } from './queryKeys';
import { useRouteParams } from '../hooks/useRouteParams';
import { urlWithParams } from '../../CommonUtils/urlWithParams';

export enum PostTypeDTO {
  Post,
  RSS,
  Notification,
  Statement,
}

export interface PostDTO {
  title: string;
  date: string;
  caption: string | null;
  content: string | null;
  link: string | null;
  isNew: boolean;
  type: PostTypeDTO | null;
}

interface NewsFeedDTO {
  posts: PostDTO[];
  previousPublishDate: string;
}

interface FetchParams {
  clientId: string;
  pageParam: string;
}

/**
 * We don't have control over the HTML generated by third parties and they can
 * include sources as relative paths.
 * As a best-effort fix, this function uses the post's supporting link as the
 * origin for sources with relative paths.
 * @param post Post with sources as relative URIs
 * @returns Post with sources as absolute URIs
 */
const fixPostContentSources = (post: PostDTO): PostDTO => {
  if (!post.link || !post.content) {
    return post;
  }

  const { origin } = new URL(post.link);

  const contentWithAbsoluteSources = post.content.replace(
    /src=["'](\/[^"']+)["']/,
    `src="${origin}$1"`,
  );

  return { ...post, content: contentWithAbsoluteSources };
};

const fetchNewsfeed = async ({ clientId, pageParam }: FetchParams): Promise<NewsFeedDTO> => {
  const newsfeed: NewsFeedDTO = await authFetch(
    urlWithParams('/Consumer/NewsFeed', {
      clientId,
      endUtcDate: pageParam,
    }),
  );

  return {
    ...newsfeed,
    posts: newsfeed.posts.map(fixPostContentSources),
  };
};

export const useNewsfeedQuery = () => {
  const { clientId } = useRouteParams();

  return useInfiniteQuery(
    newsfeedQueryKeys.newsfeed(clientId),

    ({ pageParam }) => fetchNewsfeed({ clientId, pageParam }),

    {
      getNextPageParam: lastPage => lastPage.previousPublishDate,
    },
  );
};
